import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { CONNECTED_CHANNEL } from "apprentices/worker/integrations";
import { type FormEvent, useEffect, useState } from "react";
import { toast } from "sonner";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  listIntegrations,
  openIntegration,
} from "@/jigs/integrations-capability";
import { bearAuthClient } from "@/lib/bear-auth-client";
import { RequireSignIn, useSession, useSignedInUser } from "@/lib/session";

export const Route = createFileRoute("/account")({
  component: () => (
    <RequireSignIn>
      <Account />
    </RequireSignIn>
  ),
});

const dateFormat = new Intl.DateTimeFormat(undefined, { dateStyle: "medium" });

function Account() {
  const user = useSignedInUser();
  const { signOut } = useSession();
  return (
    <main className="mx-auto flex min-h-screen max-w-2xl flex-col gap-10 px-6 py-12">
      <div className="flex items-center justify-between">
        <Link to="/" className="text-sm text-muted-foreground underline">
          Back to projects
        </Link>
        <Button variant="outline" size="sm" onClick={() => void signOut()}>
          Sign out
        </Button>
      </div>
      <section className="flex items-center gap-4">
        <Avatar className="h-12 w-12">
          <AvatarImage src={user.image ?? undefined} alt={user.name} />
          <AvatarFallback>{user.name.slice(0, 1).toUpperCase()}</AvatarFallback>
        </Avatar>
        <div>
          <h1 className="text-2xl font-semibold">{user.name}</h1>
          <p className="text-sm text-muted-foreground">{user.email}</p>
        </div>
      </section>
      <Passkeys />
      <Autodesk />
      <ApiKeys />
    </main>
  );
}

function Autodesk() {
  const user = useSignedInUser();
  const queryClient = useQueryClient();
  const accounts = useQuery({
    queryKey: ["integrations", user.id],
    queryFn: () => listIntegrations(),
  });
  const linked = accounts.data?.some((a) => a.integration === "autodesk");
  useEffect(() => {
    const channel = new BroadcastChannel(CONNECTED_CHANNEL);
    channel.onmessage = () =>
      void queryClient.invalidateQueries({
        queryKey: ["integrations", user.id],
      });
    return () => channel.close();
  }, [queryClient, user.id]);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">Autodesk</h2>
          <p className="text-sm text-muted-foreground">
            {linked
              ? "Your Autodesk account is connected."
              : "Connect your Autodesk account to work with Revit cloud models."}
          </p>
        </div>
        <Button
          disabled={accounts.isPending}
          onClick={() => openIntegration("autodesk")}
        >
          {linked ? "Reconnect Autodesk" : "Connect Autodesk"}
        </Button>
      </div>
      {accounts.isError && (
        <Alert variant="destructive">
          <AlertDescription>
            Could not load your connections. Reload this page to try again.
          </AlertDescription>
        </Alert>
      )}
    </section>
  );
}

interface PasskeySummary {
  id: string;
  name?: string | null;
  aaguid?: string | null;
  deviceType?: string;
  createdAt?: string | Date | null;
}

function passkeyLabel(
  key: PasskeySummary,
  getAuthenticatorName: (aaguid: string | undefined) => string | undefined,
): string {
  return (
    key.name?.trim() ||
    getAuthenticatorName(key.aaguid ?? undefined) ||
    (key.deviceType === "multiDevice" ? "Synced passkey" : "Security key")
  );
}

type PasskeyDialog = { kind: "add" } | { kind: "rename"; id: string };

function Passkeys() {
  const queryClient = useQueryClient();
  const passkeys = useQuery({
    queryKey: ["passkeys"],
    queryFn: async () => {
      // The name table ships only in the server plugin, which drags WebAuthn
      // server code along: load it beside the list, off the route chunk.
      const [result, { getAuthenticatorName }] = await Promise.all([
        bearAuthClient.passkey.listUserPasskeys(),
        import("@better-auth/passkey"),
      ]);
      if (result.error) throw new Error(result.error.message);
      return result.data.map((key) => ({
        ...key,
        label: passkeyLabel(key, getAuthenticatorName),
      }));
    },
  });
  const [dialog, setDialog] = useState<PasskeyDialog | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function open(next: PasskeyDialog, initialName = "") {
    setDialog(next);
    setName(initialName);
    setError(null);
  }

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!dialog) return;
    setBusy(true);
    setError(null);
    const result =
      dialog.kind === "add"
        ? await bearAuthClient.passkey.addPasskey({
            name: name.trim() || undefined,
          })
        : await bearAuthClient.passkey.updatePasskey({
            id: dialog.id,
            name: name.trim(),
          });
    setBusy(false);
    if (result?.error) {
      setError(result.error.message ?? "Could not save the passkey.");
      return;
    }
    setDialog(null);
    await queryClient.invalidateQueries({ queryKey: ["passkeys"] });
  }

  async function remove(key: PasskeySummary) {
    const result = await bearAuthClient.passkey.deletePasskey({ id: key.id });
    if (result.error) {
      toast.error(result.error.message ?? "Could not remove the passkey.");
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ["passkeys"] });
  }

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">Passkeys</h2>
          <p className="text-sm text-muted-foreground">
            Sign in with your device instead of Google. If you lose every
            passkey, sign in with Google and add a new one.
          </p>
        </div>
        <Button onClick={() => open({ kind: "add" })}>Add passkey</Button>
      </div>
      {passkeys.data?.length ? (
        <ul className="divide-y rounded-lg border">
          {passkeys.data.map((key) => (
            <li
              key={key.id}
              className="flex items-center justify-between gap-3 p-3"
            >
              <div>
                <p className="text-sm font-medium">{key.label}</p>
                {key.createdAt && (
                  <p className="text-xs text-muted-foreground">
                    Added {dateFormat.format(new Date(key.createdAt))}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    open({ kind: "rename", id: key.id }, key.label)
                  }
                >
                  Rename
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => void remove(key)}
                >
                  Remove
                </Button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">
          {passkeys.isPending ? "Loading…" : "No passkeys yet."}
        </p>
      )}
      <Dialog
        open={dialog !== null}
        onOpenChange={(isOpen) => !isOpen && !busy && setDialog(null)}
      >
        <DialogContent>
          <form className="flex flex-col gap-5" onSubmit={save}>
            <DialogHeader>
              <DialogTitle>
                {dialog?.kind === "add" ? "Add a passkey" : "Rename passkey"}
              </DialogTitle>
              <DialogDescription>
                Give this passkey a name you will recognize.
              </DialogDescription>
            </DialogHeader>
            <Input
              aria-label="Passkey name"
              aria-invalid={!!error}
              autoComplete="off"
              maxLength={80}
              placeholder="For example, MacBook or security key"
              required={dialog?.kind === "rename"}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={busy}
                onClick={() => setDialog(null)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={busy}>
                {dialog?.kind === "add" ? "Create passkey" : "Save name"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function ApiKeys() {
  const queryClient = useQueryClient();
  const keys = useQuery({
    queryKey: ["api-keys"],
    queryFn: async () => {
      const result = await bearAuthClient.apiKey.list();
      if (result.error) throw new Error(result.error.message);
      return result.data.apiKeys;
    },
  });
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [secret, setSecret] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    const result = await bearAuthClient.apiKey.create({ name: name.trim() });
    setBusy(false);
    if (result.error) {
      toast.error(result.error.message ?? "Could not create the API key.");
      return;
    }
    setSecret(result.data.key);
    await queryClient.invalidateQueries({ queryKey: ["api-keys"] });
  }

  async function revoke(keyId: string) {
    const result = await bearAuthClient.apiKey.delete({ keyId });
    if (result.error) {
      toast.error(result.error.message ?? "Could not revoke the API key.");
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ["api-keys"] });
  }

  // Reset on open, not close: the dialog animates out showing its last state.
  function openCreate() {
    setSecret(null);
    setName("");
    setCreating(true);
  }

  function close() {
    setCreating(false);
  }

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">API keys</h2>
          <p className="text-sm text-muted-foreground">
            For scripts and tools that read your work, such as a chat&apos;s
            trace. A key acts as you; revoke it when you no longer need it.
          </p>
        </div>
        <Button onClick={openCreate}>Create key</Button>
      </div>
      {keys.data?.length ? (
        <ul className="divide-y rounded-lg border">
          {keys.data.map((key) => (
            <li
              key={key.id}
              className="flex items-center justify-between gap-3 p-3"
            >
              <div>
                <p className="text-sm font-medium">{key.name || "Unnamed"}</p>
                <p className="font-mono text-xs text-muted-foreground">
                  {key.start}… · created{" "}
                  {dateFormat.format(new Date(key.createdAt))}
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => void revoke(key.id)}
              >
                Revoke
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">
          {keys.isPending ? "Loading…" : "No API keys."}
        </p>
      )}
      <Dialog
        open={creating}
        onOpenChange={(isOpen) => !isOpen && !busy && close()}
      >
        <DialogContent>
          {secret ? (
            <div className="flex flex-col gap-5">
              <DialogHeader>
                <DialogTitle>Copy your API key</DialogTitle>
                <DialogDescription>
                  This is the only time it is shown. Send it as{" "}
                  <code>Authorization: Bearer &lt;key&gt;</code>.
                </DialogDescription>
              </DialogHeader>
              <Input readOnly value={secret} className="font-mono" />
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => void navigator.clipboard.writeText(secret)}
                >
                  Copy
                </Button>
                <Button onClick={close}>Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <form className="flex flex-col gap-5" onSubmit={create}>
              <DialogHeader>
                <DialogTitle>Create an API key</DialogTitle>
                <DialogDescription>
                  Name it after where it will be used.
                </DialogDescription>
              </DialogHeader>
              <Input
                aria-label="API key name"
                autoComplete="off"
                maxLength={80}
                placeholder="For example, trace export script"
                required
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={close}>
                  Cancel
                </Button>
                <Button type="submit" disabled={busy}>
                  Create key
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
